import type {
  ProgramOption,
  ProgramOptionType,
} from '@/helpers/slotSchedulerUtil';
import {
  isSelectableForNewSlot,
  ProgramOptionTypes,
} from '@/helpers/slotSchedulerUtil.ts';
import { useLingui } from '@lingui/react/macro';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { isNonEmptyString } from '@tunarr/shared/util';
import { filter, map } from 'lodash-es';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSlotProgramOptionsContext } from '../../hooks/programming_controls/useSlotProgramOptions.ts';
import type {
  CommonSlotViewModel,
  LinkMode,
} from '../../model/CommonSlotModels.ts';
import { CustomShowSlotProgrammingForm } from './CustomShowSlotProgrammingForm.tsx';
import { FillerListSlotProgrammingForm } from './FillerListSlotProgrammingForm.tsx';
import { RedirectProgrammingForm } from './RedirectProgrammingForm.tsx';
import { ShowSearchSlotProgrammingForm } from './ShowSearchSlotProgrammingForm.tsx';
import { SlotLinkingControl } from './SlotLinkingControl.tsx';
import { SmartCollectionSlotProgrammingForm } from './SmartCollectionSlotProgrammingForm.tsx';

type EditSlotProgramProps<SlotT extends { type: ProgramOptionType }> = {
  newSlotForType: (type: ProgramOptionType) => SlotT;
  allSlots: CommonSlotViewModel[];
  onLinkSourceSlot?: (
    sourceSlotId: string,
    groupId: string,
    linkMode: LinkMode,
  ) => void;
  onUnlinkFromGroup?: (groupId: string) => void;
};

export const EditSlotProgrammingForm = <
  SlotT extends { type: ProgramOptionType },
>({
  newSlotForType,
  allSlots,
  onLinkSourceSlot,
  onUnlinkFromGroup,
}: EditSlotProgramProps<SlotT>) => {
  const { t } = useLingui();
  const { reset, control } = useFormContext<CommonSlotViewModel>();
  const [type, iterationGroup] = useWatch({
    control,
    name: ['type', 'iterationGroup'],
  });
  const programOptions = useSlotProgramOptionsContext();
  const availableTypes = useMemo(() => {
    const types = new Set<ProgramOptionType>(
      programOptions.filter(isSelectableForNewSlot).map(({ type }) => type),
    );
    // A saved slot keeps its own type listed so it stays editable after its
    // content becomes unavailable.
    types.add(type);
    return types;
  }, [programOptions, type]);

  const onlyEmptyCustomShows =
    !availableTypes.has('custom-show') &&
    programOptions.some((opt) => opt.type === 'custom-show');

  // The Select reads the form's type directly. Slot linking resets the form,
  // so a local copy would go stale and list a type with no content behind it.
  const handleTypeChange = (value: ProgramOptionType) => {
    if (value === type) {
      return;
    }

    const slot = newSlotForType(value);
    reset((prev) => ({ ...prev, ...slot }));
  };

  return (
    <>
      <FormControl fullWidth>
        <InputLabel>{t`Type`}</InputLabel>
        <Select
          label={t`Type`}
          value={type}
          onChange={(e) =>
            handleTypeChange(e.target.value as ProgramOption['type'])
          }
          disabled={isNonEmptyString(iterationGroup)}
        >
          {map(
            filter(ProgramOptionTypes, ({ value }) =>
              availableTypes.has(value),
            ),
            ({ value, description }) => (
              <MenuItem key={value} value={value}>
                {description}
              </MenuItem>
            ),
          )}
          {onlyEmptyCustomShows && (
            <MenuItem value="custom-show" disabled>
              {t`Custom Show (add programs to a custom show first)`}
            </MenuItem>
          )}
        </Select>
        <FormHelperText>
          {t`To schedule movies, add them to a custom show and choose Custom Show.`}
        </FormHelperText>
      </FormControl>
      {type === 'custom-show' && <CustomShowSlotProgrammingForm />}
      {type === 'smart-collection' && <SmartCollectionSlotProgrammingForm />}
      {type === 'filler' && <FillerListSlotProgrammingForm />}
      {type === 'show' && <ShowSearchSlotProgrammingForm />}
      {type === 'redirect' && <RedirectProgrammingForm />}

      <SlotLinkingControl
        allSlots={allSlots}
        onLinkSourceSlot={onLinkSourceSlot}
        onUnlinkFromGroup={onUnlinkFromGroup}
      />
    </>
  );
};
